import React, { useState, useEffect } from 'react';
import { userReviewAPI } from '../../services/api';
import './ReviewForm.css';

const ReviewForm = ({ userId, existingReview, onSubmitted, onUpdated, onCancel }) => {
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState(5);
  const [relationshipType, setRelationshipType] = useState('other');
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (existingReview) {
      setReviewText(existingReview.review_text || '');
      setRating(existingReview.rating || 5);
      setRelationshipType(existingReview.relationship_type || 'other');
      setCompany(existingReview.company || '');
    }
  }, [existingReview]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (reviewText.trim().length < 10) {
      setError('Review must be at least 10 characters long.');
      return;
    }

    try {
      setSubmitting(true);
      
      if (existingReview) {
        // Update existing review
        const response = await userReviewAPI.updateReview(existingReview.id, {
          review_text: reviewText,
          rating: rating,
          relationship_type: relationshipType,
          company: company,
        });

        if (response.success) {
          onUpdated();
        } else {
          setError(response.message || 'Failed to update review');
        }
      } else {
        // Create new review
        const response = await userReviewAPI.createReview({
          reviewed_user_id: userId,
          review_text: reviewText,
          rating: rating,
          relationship_type: relationshipType,
          company: company,
        });

        if (response.success) {
          onSubmitted();
        } else {
          setError(response.message || 'Failed to submit review');
        }
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err.message || 'Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (currentRating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <button
        key={i}
        type="button"
        className={`star-button ${i < currentRating ? 'filled' : ''}`}
        onClick={() => setRating(i + 1)}
        onMouseEnter={() => {
          // Optional: Add hover effect
        }}
      >
        ★
      </button>
    ));
  };

  const relationshipTypes = [
    { value: 'colleague', label: 'Colleague' },
    { value: 'manager', label: 'Manager' },
    { value: 'report', label: 'Reported to' },
    { value: 'client', label: 'Client' },
    { value: 'peer', label: 'Peer' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div className="review-form-container">
      <div className="review-form-header-section">
        <div className="review-form-header-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
        </div>
        <div className="review-form-header-content">
          <h3>{existingReview ? 'Update Your Recommendation' : 'Write a Recommendation'}</h3>
          <p className="review-form-subtitle">
            {existingReview 
              ? 'Update your recommendation to reflect your current experience.'
              : 'Share your professional experience working with this person. Your recommendation helps build their professional reputation.'}
          </p>
        </div>
        {onCancel && (
          <button type="button" className="btn-cancel" onClick={onCancel}>
            ✕
          </button>
        )}
      </div>
      <form className="review-form" onSubmit={handleSubmit}>

        {error && (
          <div className="review-form-error">{error}</div>
        )}

        <div className="review-form-group">
          <label htmlFor="rating">Rating *</label>
          <div className="rating-input">
            {renderStars(rating)}
            <span className="rating-value-text">{rating} out of 5</span>
          </div>
        </div>

        <div className="review-form-group">
          <label htmlFor="relationship_type">Your Relationship *</label>
          <select
            id="relationship_type"
            value={relationshipType}
            onChange={(e) => setRelationshipType(e.target.value)}
            className="review-form-select"
            required
          >
            {relationshipTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="review-form-group">
          <label htmlFor="company">Company/Organization *</label>
          <input
            type="text"
            id="company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="e.g., Microsoft, Google, ABC Company"
            className="review-form-input"
            maxLength={255}
            required
          />
          <p className="review-form-help-text">Which company were you both associated with?</p>
        </div>

        <div className="review-form-group">
          <label htmlFor="review_text">Your Review *</label>
          <textarea
            id="review_text"
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            placeholder="Share your experience working with this person..."
            className="review-form-textarea"
            rows={6}
            minLength={10}
            maxLength={2000}
            required
          />
          <div className="review-form-char-count">
            {reviewText.length} / 2000 characters
          </div>
        </div>

        <div className="review-form-actions">
          {onCancel && (
            <button 
              type="button" 
              className="btn-cancel-form"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          )}
          <button 
            type="submit" 
            className="btn-submit-review"
            disabled={submitting || reviewText.trim().length < 10}
          >
            {submitting ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;
